'use client';

/**
 * ColorPickerField Component
 *
 * A GlassTextField with a hex color input and a visual color swatch preview
 * displayed as a startAdornment. Validates #RRGGBB format.
 */

import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import React, { forwardRef, useCallback, useMemo } from 'react';
import { GlassTextField } from '../GlassTextField';

// ============================================================================
// Types
// ============================================================================

export interface ColorPickerFieldProps {
  /** Current hex color value (e.g. '#FF0000') */
  value: string;
  /** Called when color changes */
  onChange: (color: string) => void;
  /** Field label */
  label?: string;
  /** Disabled state */
  disabled?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

function isValidHexColor(color: string): boolean {
  return HEX_COLOR_REGEX.test(color);
}

// ============================================================================
// Component
// ============================================================================

export const ColorPickerField = forwardRef<HTMLInputElement, ColorPickerFieldProps>(
  ({ value, onChange, label, disabled = false }, ref) => {
    const isValid = useMemo(() => isValidHexColor(value), [value]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value);
      },
      [onChange],
    );

    const swatchColor = isValid ? value : 'transparent';

    return (
      <GlassTextField
        ref={ref}
        label={label}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        placeholder="#000000"
        error={value.length > 0 && !isValid}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: swatchColor,
                    border: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                  }}
                />
              </InputAdornment>
            ),
          },
          htmlInput: {
            maxLength: 7,
          },
        }}
      />
    );
  },
);

ColorPickerField.displayName = 'ColorPickerField';

export default ColorPickerField;
